import React, { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import MainCard from '../../components/MainCard';
import { IPeriodTotals } from '../../apis/models/transactions';

type Metric = 'income' | 'expense';

interface PeriodComparisonChartProps {
  periodA: IPeriodTotals;
  periodB: IPeriodTotals;
  labelA: string;
  labelB: string;
  currency?: string;
}

const formatAmount = (value: number) => Math.round(value).toLocaleString();

const PeriodComparisonChart: React.FC<PeriodComparisonChartProps> = ({
  periodA,
  periodB,
  labelA,
  labelB,
  currency = '$',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [metric, setMetric] = useState<Metric>('income');

  const categories = useMemo(() => {
    const maxLen = Math.max(periodA.daily.length, periodB.daily.length);
    // "D1, D2..." / "J1, J2..." — the full "Day 1" label was too wide once both series' legends were shown.
    return Array.from({ length: maxLen }, (_, i) => `${t('report.day_short_prefix')}${i + 1}`);
  }, [periodA.daily.length, periodB.daily.length, t]);

  const series = useMemo(() => {
    const maxLen = Math.max(periodA.daily.length, periodB.daily.length);
    const toSeries = (period: IPeriodTotals) =>
      Array.from({ length: maxLen }, (_, i) => period.daily[i]?.[metric] ?? null);
    return [
      { name: labelA, data: toSeries(periodA) },
      { name: labelB, data: toSeries(periodB) },
    ];
  }, [periodA, periodB, metric, labelA, labelB]);

  // Blue vs orange gives clear separation even on presets where primary/secondary are close in hue.
  const options: any = useMemo(
    () => ({
      chart: { type: 'line', toolbar: { show: false } },
      dataLabels: { enabled: false },
      // markers keep single-day / very short periods visible instead of rendering as an empty canvas
      markers: { size: categories.length <= 3 ? 5 : 0, hover: { size: 7 }, strokeWidth: 0 },
      stroke: { width: [3, 3], dashArray: [0, 6], curve: 'straight' },
      colors: [theme.palette.primary.main, theme.palette.warning.main],
      grid: { strokeDashArray: 4, borderColor: theme.palette.divider },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: categories.map(() => theme.palette.text.secondary) } },
      },
      yaxis: {
        labels: {
          style: { colors: [theme.palette.text.secondary] },
          formatter: (value: number) => formatAmount(value),
        },
      },
      legend: { position: 'top' },
      tooltip: {
        x: { show: true },
        y: { formatter: (value: number) => `${currency}${formatAmount(value)}` },
      },
    }),
    [categories, theme, currency]
  );

  const handleMetricChange = (_: React.MouseEvent, value: Metric | null) => {
    if (value) setMetric(value);
  };

  return (
    <MainCard
      title={t('report.comparison_chart_title')}
      secondary={
        <ToggleButtonGroup value={metric} exclusive onChange={handleMetricChange} size="small">
          <ToggleButton value="income">{t('report.total_income')}</ToggleButton>
          <ToggleButton value="expense">{t('report.total_expenses')}</ToggleButton>
        </ToggleButtonGroup>
      }
      sx={{ mb: 2 }}
    >
      <Box sx={{ mt: 1 }}>
        {categories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('report.no_daily_data')}
          </Typography>
        ) : (
          // react-apexcharts diffs props via JSON.stringify and only calls updateSeries/updateOptions —
          // that path is unreliable when series length/categories change between periods, so force a
          // full remount keyed on the actual period identity instead of trusting its internal diffing.
          <ReactApexChart
            key={`${periodA.start}_${periodA.end}_${periodB.start}_${periodB.end}_${metric}`}
            options={options}
            series={series}
            type="line"
            height={300}
          />
        )}
      </Box>
    </MainCard>
  );
};

export default PeriodComparisonChart;
